<template>
    <div class="relative bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />

        <h2 class="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Goods Receipt Report</h2>

        <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 mb-6 p-4 border rounded-lg bg-gray-50 items-start">
            <div class="flex items-center space-x-2">
                <div class="flex-grow">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="poNumber">PO #</label>
                    <v-select id="poNumber" v-model="filters.poId" :options="poOptions" :reduce="po => po.id"
                        label="poNumber" placeholder="-- Pilih PO --" :disabled="filters.allPos"
                        class="input-border bg-white">
                    </v-select>
                </div>
                <div class="pt-8">
                    <input type="checkbox" id="allPosCheck" v-model="filters.allPos" class="mr-2 h-4 w-4">
                    <label for="allPosCheck" class="text-sm text-gray-700 font-bold">ALL</label>
                </div>
            </div>
            <div>
                <label class="block text-gray-700 text-sm font-bold mb-2">Periode Submit</label>
                <div class="flex items-center space-x-2">
                    <input type="date" v-model="filters.startDate"
                        class="input-border shadow-sm w-full py-2 px-3 text-gray-700">
                    <span>to</span>
                    <input type="date" v-model="filters.endDate"
                        class="input-border shadow-sm w-full py-2 px-3 text-gray-700">
                </div>
            </div>
            <div class="flex items-end justify-start space-x-4 h-full">
                <button @click="fetchReportData"
                    class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg">
                    <i class="fas fa-eye mr-2"></i>Preview
                </button>
                <button @click="exportToExcel"
                    class="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg">
                    <i class="fas fa-file-excel mr-2"></i>Export to Excel
                </button>
            </div>
        </div>

        <h3 class="text-lg font-semibold my-4 text-gray-700">Hasil Laporan</h3>
        <div class="overflow-x-auto border rounded-lg">
            <table class="min-w-full bg-white text-sm">
                <thead class="bg-gray-200">
                    <tr>
                        <th class="py-3 px-4 text-left text-xs font-bold text-gray-600 uppercase">No</th>
                        <th class="py-3 px-4 text-left text-xs font-bold text-gray-600 uppercase">PO #</th>
                        <th class="py-3 px-4 text-left text-xs font-bold text-gray-600 uppercase">Cust Name</th>
                        <th class="py-3 px-4 text-left text-xs font-bold text-gray-600 uppercase">PO Type</th>
                        <th class="py-3 px-4 text-right text-xs font-bold text-gray-600 uppercase">Total PO</th>
                        <th class="py-3 px-4 text-right text-xs font-bold text-gray-600 uppercase">GR Balance</th>
                        <th class="py-3 px-4 text-center text-xs font-bold text-gray-600 uppercase">Total GR (%)</th>
                        <th class="py-3 px-4 text-center text-xs font-bold text-gray-600 uppercase border-l">GR %</th>
                        <th class="py-3 px-4 text-right text-xs font-bold text-gray-600 uppercase">GR Amount</th>
                        <th class="py-3 px-4 text-center text-xs font-bold text-gray-600 uppercase">Submit Date</th>
                        <th class="py-3 px-4 text-center text-xs font-bold text-gray-600 uppercase">Received Date</th>
                        <th class="py-3 px-4 text-center text-xs font-bold text-gray-600 uppercase">Aging (Days)</th>
                        <th class="py-3 px-4 text-center text-xs font-bold text-gray-600 uppercase">Received Finance
                        </th>
                        <th class="py-3 px-4 text-left text-xs font-bold text-gray-600 uppercase">User Finance</th>
                        <th class="py-3 px-4 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                    </tr>
                </thead>

                <tbody v-if="reportData.length > 0">
                    <template v-for="(master, index) in reportData" :key="master.poId">
                        <tr v-for="(detail, detailIndex) in master.details" :key="detail.submitDate"
                            class="hover:bg-gray-50 border-b">
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top border-r">{{ index + 1 }}</td>
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top border-r">{{ master.poNumber }}</td>
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top border-r">{{ master.customerName }}</td>
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top border-r">{{ master.poType }}</td>
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top text-right border-r">{{ formatCurrency(master.totalPo) }}
                            </td>
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top text-right border-r">{{ formatCurrency(master.grBalance) }}
                            </td>
                            <td v-if="detailIndex === 0" :rowspan="master.details.length"
                                class="py-2 px-4 align-top text-center font-bold border-r">{{
                                    master.totalGrPercentage.toFixed(2) }}%</td>
                            <td class="py-2 px-4 text-center">{{ detail.grPercentage }}%</td>
                            <td class="py-2 px-4 text-right">{{ formatCurrency(detail.grAmount) }}</td>
                            <td class="py-2 px-4 text-center">{{ formatDate(detail.submitDate) }}</td>
                            <td class="py-2 px-4 text-center">{{ formatDate(detail.receivedDate) }}</td>
                            <td class="py-2 px-4 text-center">{{ detail.aging !== null ? detail.aging : '-' }}</td>
                            <td class="py-2 px-4 text-center">{{ formatDate(detail.receivedFinanceDate) }}</td>
                            <td class="py-2 px-4">{{ detail.financeUser }}</td>
                            <td class="py-2 px-4">
                                <span :class="getStatusClass(detail.status)"
                                    class="px-2 py-1 text-xs font-semibold rounded-full">
                                    {{ detail.status }}
                                </span>
                            </td>
                        </tr>
                    </template>
                </tbody>

                <tbody v-else>
                    <tr v-if="!reportData.length && !isFirstLoad">
                        <td colspan="16" class="text-center py-6 text-gray-500">Tidak ada data yang ditemukan sesuai
                            filter.</td>
                    </tr>
                    <tr v-if="isFirstLoad">
                        <td colspan="16" class="text-center py-6 text-gray-500">Silakan pilih filter dan klik "Preview"
                            untuk
                            menampilkan data.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';
import * as XLSX from 'xlsx';

export default {
    name: 'GoodsReceiptReport',
    components: {
        'v-select': vSelect,
        MonkeyLoader,
    },
    data() {
        return {
            isLoading: false,
            isFirstLoad: true,
            filters: {
                poId: null,
                allPos: true,
                startDate: this.getThirtyDaysAgo(), // Diubah
                endDate: this.getTodayDate(),       // Diubah
            },
            poOptions: [],
            reportData: [],
        };
    },
    methods: {
        getTodayDate() {
            return new Date().toISOString().split('T')[0];
        },
        getThirtyDaysAgo() {
            const date = new Date();
            date.setDate(date.getDate() - 30);
            return date.toISOString().split('T')[0];
        },
        formatCurrency(value) {
            if (value == null) return 'Rp 0';
            return `Rp ${new Intl.NumberFormat('id-ID').format(value)}`;
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        getStatusClass(status) {
            if (status === 'Completed') return 'bg-green-100 text-green-800';
            if (status === 'Pending') return 'bg-yellow-100 text-yellow-800';
            return 'bg-gray-100 text-gray-800';
        },
        async loadPoOptions() {
            const apiUrl = `${window.location.origin}/api/all-gr-pos`;
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
                this.poOptions = response.data;
            } catch (error) {
                console.error("Error fetching PO options:", error);
                Swal.fire('Error', 'Gagal memuat daftar PO.', 'error');
            }
        },
        async fetchReportData() {
            this.isLoading = true;
            this.isFirstLoad = false;
            const reportApiUrl = `${window.location.origin}/api/reports/goods-receipt`;

            try {
                const token = localStorage.getItem('token');
                const params = {
                    poId: this.filters.allPos ? null : this.filters.poId,
                    startDate: this.filters.startDate,
                    endDate: this.filters.endDate,
                };

                const response = await axios.get(reportApiUrl, { headers: { Authorization: `Bearer ${token}` }, params });
                this.reportData = response.data;

                if (this.reportData.length === 0) {
                    Swal.fire('Info', 'Tidak ada data yang ditemukan untuk filter yang dipilih.', 'info');
                }
            } catch (error) {
                console.error("Error fetching report data:", error);
                Swal.fire('Error', 'Gagal memuat data laporan.', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        exportToExcel() {
            if (this.reportData.length === 0) {
                Swal.fire('Perhatian', 'Tidak ada data untuk diekspor.', 'warning');
                return;
            }
            const dataToExport = this.reportData.map((item, index) => ({
                'No': index + 1,
                'PO #': item.poNumber,
                'Cust Name': item.customerName,
                'PO Type': item.poType,
                'Total PO': item.totalPo,
                'GR Balance': item.grBalance,
                'GR %': item.grPercentage,
                'GR Amount': item.grAmount,
                'Submit Date': this.formatDate(item.submitDate),
                'Received Date': this.formatDate(item.receivedDate),
                'Aging (Days)': item.aging,
                'Received Finance': this.formatDate(item.receivedFinanceDate),
                'User Finance': item.financeUser,
                'Status': item.status,
            }));
            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "GoodsReceiptReport");
            XLSX.writeFile(workbook, "GoodsReceiptReport.xlsx");
        },
    },
    watch: {
        'filters.allPos'(newValue) {
            if (newValue) { this.filters.poId = null; }
        },
    },
    mounted() {
        this.loadPoOptions();
    }
};
</script>

<style scoped>
.input-border {
    border: 1px solid #d2d6dc;
    border-radius: 0.375rem;
}

.input-border:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
    outline: none;
}

:deep(.vs__dropdown-toggle) {
    border: 1px solid #d2d6dc;
    border-radius: 0.375rem;
    padding: 4px 6px;
}

:deep(.vs--open .vs__dropdown-toggle) {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
}
</style>